import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Heart, ShieldAlert, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  getDisclaimerStatus,
  acceptDisclaimer,
} from "@/lib/disclaimer.functions";

const STORAGE_KEY = "rt_disclaimer_ack_v1";

const AUTH_PATHS = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/disclaimer",
  // One-tap email links (short feedback forms) should open straight away.
  "/live-sessions-survey",
];

export function DisclaimerGate() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [needsAck, setNeedsAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const fetchStatus = useServerFn(getDisclaimerStatus);
  const persistAccept = useServerFn(acceptDisclaimer);

  const onAuthRoute = AUTH_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

  useEffect(() => {
    let cancelled = false;

    async function evaluate(userId: string | null) {
      // Guests: localStorage only
      if (!userId) {
        if (cancelled) return;
        setAuthed(false);
        const ack = typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY);
        setNeedsAck(!ack);
        return;
      }

      setAuthed(true);
      const localAck = localStorage.getItem(STORAGE_KEY);

      // If the user already acked as a guest, auto-persist to server
      // so the modal does not re-fire right after signup/login.
      if (localAck) {
        if (cancelled) return;
        setNeedsAck(false);
        // Persist in the background — a stuck connection must not hold the
        // notice open.
        void persistAccept().catch(() => {
          // localStorage ack is still valid UX-wise
        });
        return;
      }

      // No local ack — check server
      try {
        const { acceptedAt } = await fetchStatus();
        if (cancelled) return;
        if (acceptedAt) {
          localStorage.setItem(STORAGE_KEY, "1");
          setNeedsAck(false);
        } else {
          // The user may have accepted while this server check was still in
          // flight — a local ack written after evaluate() started must win,
          // otherwise the notice re-opens right after "I Understand & Continue".
          setNeedsAck(!localStorage.getItem(STORAGE_KEY));
        }
      } catch {
        // fall back to localStorage if server check fails
        const ack = localStorage.getItem(STORAGE_KEY);
        if (!cancelled) setNeedsAck(!ack);
      }
    }

    supabase.auth.getSession().then(({ data }) => evaluate(data.session?.user?.id ?? null));
    const handler = (e: Event) => {
      const session = (e as CustomEvent).detail?.session;
      evaluate(session?.user?.id ?? null);
    };
    window.addEventListener("rt-auth-change", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("rt-auth-change", handler);
    };
  }, [fetchStatus]);

  if (!needsAck || onAuthRoute) return null;

  async function handleAccept() {
    setSubmitting(true);
    localStorage.setItem(STORAGE_KEY, "1");
    // Close immediately — the server save happens in the background so a
    // slow or stuck connection can never leave the page frozen behind this
    // notice.
    setNeedsAck(false);
    setSubmitting(false);
    if (authed) {
      void persistAccept().catch(() => {
        // localStorage still remembers the acknowledgement.
      });
    }
  }

  // On the Trauma Response Wheel page the wheel sits at the top of the
  // screen, so pin the welcome card to the bottom and keep it compact —
  // the wheel must stay visible and tappable-looking behind it.
  const isWheelPage = pathname === "/trauma-response-wheel";

  return (
    <div
      className={
        isWheelPage
          ? "fixed inset-0 z-[100] flex items-end justify-center bg-foreground/30 px-3 pb-3 pt-24"
          : "fixed inset-0 z-[100] flex items-end justify-center bg-foreground/40 px-4 py-6 backdrop-blur-sm sm:items-center"
      }
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="disclaimer-title"
        className="w-full max-w-lg overflow-hidden rounded-3xl border border-border bg-card shadow-2xl"
      >
        <div
          className={`border-b border-border bg-[var(--sand,#F7F3EA)] px-6 ${
            isWheelPage ? "py-3" : "py-5"
          }`}
        >
          {!isWheelPage && (
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-background/60 px-3 py-1 text-xs font-medium text-foreground/70">
              <Heart className="h-3.5 w-3.5" /> A Gentle Welcome
            </div>
          )}
          <h2
            id="disclaimer-title"
            className={`font-serif text-foreground ${isWheelPage ? "text-lg" : "text-2xl"}`}
          >
            Welcome to Recovery Trauma™
          </h2>
        </div>

        <div
          className={`space-y-4 overflow-y-auto px-6 text-sm leading-relaxed text-foreground/80 ${
            isWheelPage ? "max-h-[16vh] py-3" : "max-h-[55vh] py-5"
          }`}
        >
          <p>
            Recovery Trauma™ provides educational content, self-help tools,
            wellness resources, recovery support, and community features
            designed to support your personal recovery journey.
          </p>
          <p>
            <strong className="font-semibold text-foreground">
              Recovery Trauma™ is not therapy, medical treatment, crisis
              support, or a substitute for professional mental health care.
            </strong>
          </p>
          <p>
            The app does not diagnose, treat, cure, or prevent any mental
            health condition.
          </p>
          <div className="flex gap-3 rounded-2xl border border-border bg-[var(--sage,#E6F0E8)]/40 p-4">
            <ShieldAlert className="mt-0.5 h-5 w-5 flex-none text-foreground/70" />
            <p className="text-xs leading-relaxed">
              If you are experiencing a mental health crisis, suicidal
              thoughts, self-harm thoughts, or require immediate support,
              please contact emergency services, a crisis hotline, or a
              qualified mental health professional in your area.
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            By continuing, you acknowledge that you understand and accept
            these terms.
          </p>
        </div>

        <div
          className={`flex flex-col gap-2 border-t border-border bg-background px-6 sm:flex-row-reverse sm:items-center sm:justify-between ${
            isWheelPage ? "py-2.5" : "py-4"
          }`}
        >
          <button
            type="button"
            onClick={handleAccept}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
          >
            ✓ I Understand &amp; Continue
          </button>
          <Link
            to="/disclaimer"
            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
          >
            View Full Disclaimer <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
